<template>
  <div class="empty-state-container">
    <div class="empty-state-content">
      <div class="empty-state-icon">
        <i class="pi pi-search"></i>
        <div class="empty-state-icon-bg"></div>
      </div>
      <div class="empty-state-info">
        <h3 class="empty-state-title">{{ title }}</h3>
        <p class="empty-state-description">
          {{ description }}
        </p>
      </div>
      <div class="empty-state-actions">
        <Button
          v-if="showClearFilters"
          class="clear-filters-button"
          @click="$emit('clear-filters')"
        >
          <i class="pi pi-filter-slash"></i>
          Clear Filters
        </Button>
        <Button class="all-jobs-button" @click="$emit('view-all')">
          <i class="pi pi-list"></i>
          {{ viewAllButtonText }}
        </Button>
      </div>
      <div class="empty-state-suggestions" v-if="showClearFilters">
        <h4>Suggestions:</h4>
        <ul>
          <li><i class="pi pi-check"></i> Check your spelling</li>
          <li><i class="pi pi-filter"></i> Try different filters</li>
          <li><i class="pi pi-search"></i> Broaden your search criteria</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmptyState',
  props: {
    showClearFilters: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'No Jobs Found',
    },
    description: {
      type: String,
      default:
        "We couldn't find any job listings matching your criteria. Please try different search parameters.",
    },
    viewAllButtonText: {
      type: String,
      default: 'View All Jobs',
    },
  },
  computed: {
    buttonText() {
      return this.showClearFilters ? 'View All Jobs' : 'Check Again'
    },
  },
  emits: ['clear-filters', 'view-all'],
}
</script>

<style lang="scss" scoped>
.empty-state-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
  padding: 3rem;
  margin: 2rem auto;
  max-width: 1440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(
      89.17deg,
      #003e75 0%,
      #68a8e0 100%,
      #4d94d2 100%,
      #4d94d2 100%
    );
  }
}

.empty-state-content {
  text-align: center;
  max-width: 600px;
  animation: fadeIn 0.5s ease-out;
}

.empty-state-icon {
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;

  i {
    font-size: 48px;
    color: #015cac;
    position: relative;
    z-index: 2;
  }

  .empty-state-icon-bg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 96px;
    height: 96px;
    background: rgba(1, 92, 172, 0.1);
    border-radius: 50%;
    z-index: 1;
  }
}

.empty-state-info {
  margin-bottom: 2rem;
}

.empty-state-title {
  font-size: 28px;
  font-weight: 700;
  color: #103660;
  margin-bottom: 1rem;
  letter-spacing: -0.5px;
}

.empty-state-description {
  font-size: 16px;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.empty-state-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;

  button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;

    i {
      font-size: 16px;
    }
  }

  .clear-filters-button {
    background: rgba(252, 108, 42, 0.1);
    color: #fc6c2a;
    border: none;

    &:hover {
      background: #fc6c2a;
      color: #ffffff;
    }
  }

  .all-jobs-button {
    background: rgba(1, 92, 172, 0.1);
    color: #015cac;
    border: none;

    &:hover {
      background: #015cac;
      color: #ffffff;
    }
  }
}

.empty-state-suggestions {
  text-align: left;
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  animation: slideUp 0.5s ease-out;

  h4 {
    color: #103660;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #666;
      margin-bottom: 0.75rem;
      font-size: 14px;

      i {
        color: #015cac;
        font-size: 14px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .empty-state-container {
    margin: 1rem;
    padding: 2rem 1rem;
    min-height: 400px;
  }

  .empty-state-icon {
    i {
      font-size: 36px;
    }

    .empty-state-icon-bg {
      width: 72px;
      height: 72px;
    }
  }

  .empty-state-title {
    font-size: 24px;
  }

  .empty-state-description {
    font-size: 14px;
  }

  .empty-state-actions {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0 1rem;

    button {
      width: 100%;
      justify-content: center;
    }
  }

  .empty-state-suggestions {
    margin: 0 1rem;
    padding: 1rem;
  }
}
</style>
